// @flow
import { createSelector } from "reselect";
import type { PublicIdData } from "uploader";
import consts from "../common/consts";

const selectQueues = (state: Object) => state.uploader.queues;

const getLastQueue = (state: Object) => {
	const { queues } = state.uploader;
	return queues[queues.length - 1];
};

const getActiveQueue = (state: Object) => {
	const activeQueues = state.uploader.queues.filter((q) => q.status === consts.QUEUE_STATUS.NONE);
	//give priority to queue with files in it (retry)
	return activeQueues.find((q) => q.ids.length) || activeQueues[0];
};

const getLastActiveQueue = (state: Object) => [...state.uploader.queues].reverse().find((q) => q.status === consts.QUEUE_STATUS.NONE);

const getRunningQueue = (state: Object) => state.uploader.queues.find((q) => q.status === consts.QUEUE_STATUS.RUNNING);

const selectLatestConfig: Object => ?Object = createSelector(
	getLastQueue,
	(queue) => queue && (queue.configOverride || queue.config)
);

const selectUploadOptions = (state: Object) => ({
	publicId: state.uploader.uploadPublicId,
	tags: state.uploader.uploadTags,
	uploadPreset: state.uploader.uploadPreset,
});

const selectShowOnStart = (state: Object) => state.uploader.showOnStart;

const selectWidgetId = (state: Object) => state.uploader.id;

const selectUploads: Object => { files: Object, filesData: Object } = createSelector(
	selectQueues,
	(queues: Object[]) =>
		queues.reduce(
			(res, queue) => {
				res.files = {
					...res.files,
					...queue.files,
				};

				res.filesData = {
					...res.filesData,
					...queue.filesData,
				};

				return res;
			},
			{ files: {}, filesData: {} }
		)
);

const selectUploadsData: Object => Object = createSelector(
	selectQueues,
	(queues) =>
		queues
			? queues.reduce(
					(res, queue) => ({
						...res,
						...queue.filesData,
					}),
					{}
			  )
			: {}
);

const selectConsoleInfo: Object => Object = createSelector(
	(state) => state.uploader.onConsole,
	(state) => state.uploader.onDevConsole,
	(onConsole, onDevConsole) => ({
		onConsole,
		onDevConsole,
	})
);

const selectCloudInfo: Object => Object = createSelector(
	(state) => state.uploader.cloudInfo,
	selectConsoleInfo,
	(info, { onConsole, onDevConsole }) => ({
		info,
		onConsole,
		onDevConsole,
	})
);

const selectSuccessCounter = (state: Object) => state.uploader.successCounter;

const selectRunningQueuePaused: Object => Object = createSelector(
	getRunningQueue,
	(queue) =>
		queue
			? queue.ids.reduce((res, id) => {
					if (queue.filesData[id].paused) {
						res[id] = queue.files[id];
					}

					return res;
			  }, {})
			: {}
);

const selectFilesFromQueues: Object => Object = createSelector(
	selectQueues,
	(queues) =>
		queues.reduce((res, queue) => {
			queue.ids.forEach((id) => {
				res[id] = {
					...queue.filesData[id],
					getPreviewObject: queue.files[id].getPreviewObject,
				};
			});

			return res;
		}, {})
);

const getQueueMeta = (queue) =>
	queue && {
		id: queue.queueId,
		config: queue.config,
		configOverride: queue.configOverride,
		sameConfig: queue.sameConfig,
		status: queue.status,
		pendingPreCalls: queue.pendingPreCalls,
		ids: queue.ids,
	};

const selectActiveQueueMeta: Object => ?Object = createSelector(
	getActiveQueue,
	getQueueMeta
);

const selectRunningQueueMeta: Object => ?Object = createSelector(
	getRunningQueue,
	getQueueMeta
);

const selectActiveQueueFilesIds: Object => ?(string[]) = createSelector(
	getActiveQueue,
	(queue) => queue && queue.ids
);

const selectRunningQueueFilesIds: Object => ?(string[]) = createSelector(
	getRunningQueue,
	(queue) => queue && queue.ids
);

const selectLastQueueMeta: Object => Object = createSelector(
	getLastQueue,
	getQueueMeta
);

const selectShowCounter = (state: Object) => state.uploader.showCounter;

const selectHideData = (state: Object) => ({
	options: state.uploader.hideOptions,
	counter: state.uploader.hideCounter,
});

const selectMinimizeCounter = (state: Object) => state.uploader.minimizeCounter;

const selectShowData = (state: Object) => ({
	source: state.uploader.source,
	options: state.uploader.showOptions,
});

const selectRetrievedTags = (state: Object) => state.uploader.retrievedTags;

const selectRetrievedUploadPresets = (state: Object) => state.uploader.retrievedUploadPresets;

const selectHasActiveUploadFile = (state: Object) =>
	!!state.uploader.queues.find((q) => !!Object.values(q.filesData).find((fd: any) => !fd.done && !fd.aborted && !fd.failed));

const selectClientUploadsBatch = (state: Object) => state.uploader.clientUploadBatches[0];

const selectFileQueueIndex = (state: Object) => state.uploader.fileQueueIndex;

const selectFileVersions = (state: Object) => state.uploader.fileVersions;

const selectPublicIdCounters = (state: Object) => state.uploader.publicIdCounters;

const selectPublicIdWithCounter: Object => ?{ publicId: string, counter: number } = createSelector(
	selectUploadOptions,
	selectPublicIdCounters,
	(options, counters): ?PublicIdData =>
		options.publicId ? {
					publicId: options.publicId,
					counter: counters[options.publicId] || 0,
			  } : null
);

const selectActiveQueueFolder: Object => string = createSelector(
	getLastActiveQueue,
	(queue) => {
		const config = queue && (queue.configOverride || queue.config);
		return config && (config.folder ?? config.assetFolder);
	}
);

const selectIsClearQueueInProgress = (state: Object) => state.uploader.clearQueuesInProgress;

export {
	selectLatestConfig,
	selectUploadOptions,
	selectShowOnStart,
	selectUploads,
	selectUploadsData,
	selectWidgetId,
	selectCloudInfo,
	selectRunningQueuePaused,
	selectFilesFromQueues,
	selectActiveQueueMeta,
	selectLastQueueMeta,
	selectShowCounter,
	selectHideData,
	selectMinimizeCounter,
	selectShowData,
	selectQueues,
	selectActiveQueueFilesIds,
	selectRunningQueueMeta,
	selectRunningQueueFilesIds,
	selectRetrievedTags,
	selectRetrievedUploadPresets,
	selectHasActiveUploadFile,
	selectClientUploadsBatch,
	selectFileQueueIndex,
	selectFileVersions,
	selectPublicIdWithCounter,
	selectActiveQueueFolder,
	selectSuccessCounter,
	selectIsClearQueueInProgress,
	selectConsoleInfo,
};
